import type { ApiResource } from "@/vuex/modules/apiDocs";
import type { ApiKeyStatus } from "@/vuex/modules/apiKeys";
import type { ApiError } from "../Webhooks/webhooksHub";
import { apiError } from "../Webhooks/webhooksHub";

export const API_ACCESS_PATH = "/marketplace/api-access";

export const API_KEYS_READ_ONLY_COPY =
  "Your account is read-only, so API keys can be revoked but not created or rolled.";

// The webhooks hub's error parsing, with this page's read-only line.
export function apiKeyError(error: unknown, fallback: string): ApiError {
  const parsed = apiError(error, fallback);
  return parsed.code === "account_read_only"
    ? { ...parsed, message: API_KEYS_READ_ONLY_COPY }
    : parsed;
}

const API_KEY_STATUS_STYLES: Record<
  ApiKeyStatus,
  { label: string; pill: string }
> = {
  active: { label: "Active", pill: "bg-green-50 text-green-700" },
  expired: { label: "Expired", pill: "bg-yellow-50 text-yellow-600" },
  revoked: { label: "Revoked", pill: "bg-gray-100 text-gray-600" },
  disabled: { label: "Disabled", pill: "bg-gray-200 text-gray-700" }
};

export function apiKeyStatusStyle(status: string) {
  return (
    API_KEY_STATUS_STYLES[status as ApiKeyStatus] ?? {
      label: status,
      pill: "bg-gray-100 text-gray-600"
    }
  );
}

export function expiryChoiceLabel(days: number | null): string {
  return days === null ? "Never" : `In ${days} days`;
}

export const API_KEY_SHOWN_ONCE =
  "Copy this key now. You won't be able to see it again: Sera keeps only a fingerprint of it.";

// Stands in for a key in the Docs tab's examples: a key is shown only once,
// when it's created, so the page never has one to fill in.
export const API_KEY_PLACEHOLDER = "sera_ik_YOUR_KEY";

// A page (and section) of the Docs tab. The section is a query param, not a
// #hash: the SPA's link interceptor drops the hash.
export function docsHref(page: string, section?: string | null): string {
  const query = new URLSearchParams({ tab: "docs", page });
  if (section) query.set("section", section);
  return `${API_ACCESS_PATH}?${query.toString()}`;
}

// A curl that reads one record, with the id to fill in written as the event
// field that carries it (CUSTOMER_ID).
export function curlFor(baseUrl: string, resource: ApiResource): string {
  const path = resource.path.replace(
    `:${resource.param}`,
    resource.param.toUpperCase()
  );
  return `curl ${baseUrl}${path} \\\n  -H "Authorization: Bearer ${API_KEY_PLACEHOLDER}"`;
}
